import { supabase } from "@/lib/supabase";
import { resolveDisplayName, fetchProfileMap } from "@/lib/profileDisplay";
import { fetchSquadByJoinCode } from "@/lib/squad";
import { recordDissolvedSquad } from "@/lib/dissolvedSquads";
import {
  dismissLeftSquad,
  updateLeftSquad,
  type LeftSquad,
} from "@/lib/leftSquads";

const RATE_LIMIT_MS = 24 * 60 * 60 * 1000;

export type RejoinRequestStatus = "pending" | "approved" | "denied" | "expired";

export type SquadPendingRejoinRequest = {
  id: string;
  squad_id: string;
  user_id: string;
  status: RejoinRequestStatus;
  requested_at: string;
  userName: string;
  userAvatar?: string;
};

export type RejoinAdminErrorCode =
  | "request_not_found"
  | "forbidden"
  | "already_resolved"
  | "unknown";

export class RejoinAdminError extends Error {
  readonly code: RejoinAdminErrorCode;

  constructor(code: RejoinAdminErrorCode, message?: string) {
    const defaults: Record<RejoinAdminErrorCode, string> = {
      request_not_found: "Anfrage nicht gefunden.",
      forbidden: "Keine Berechtigung.",
      already_resolved: "Anfrage wurde bereits bearbeitet.",
      unknown: "Aktion fehlgeschlagen.",
    };
    super(message ?? defaults[code]);
    this.name = "RejoinAdminError";
    this.code = code;
  }
}

function parseAdminRpcCode(message: string): RejoinAdminErrorCode {
  const lower = message.toLowerCase();
  if (lower.includes("request_not_found")) return "request_not_found";
  if (lower.includes("forbidden")) return "forbidden";
  if (lower.includes("already_resolved")) return "already_resolved";
  return "unknown";
}

export const squadPendingRejoinRequestsKey = (squadId: string) =>
  ["squad-pending-rejoin-requests", squadId] as const;

export async function fetchPendingRejoinRequests(
  squadId: string,
): Promise<SquadPendingRejoinRequest[]> {
  const { data, error } = await supabase
    .from("squad_rejoin_requests")
    .select("id, squad_id, user_id, status, requested_at")
    .eq("squad_id", squadId)
    .eq("status", "pending")
    .order("requested_at", { ascending: true });

  if (error) throw error;
  const rows = data ?? [];
  if (rows.length === 0) return [];

  const userIds = [...new Set(rows.map((r) => r.user_id))];
  const profileMap = await fetchProfileMap(userIds);

  return rows.map((row) => {
    const profile = profileMap.get(row.user_id);
    const userName = resolveDisplayName(profile);
    return {
      id: row.id,
      squad_id: row.squad_id,
      user_id: row.user_id,
      status: row.status as RejoinRequestStatus,
      requested_at: row.requested_at,
      userName,
      userAvatar: profile?.avatar_url ?? undefined,
    };
  });
}

export async function approveSquadRejoinRequest(
  requestId: string,
): Promise<{ squad_id: string; user_id: string }> {
  const { data, error } = await supabase.rpc("approve_squad_rejoin", {
    p_request_id: requestId,
  });
  if (error) throw new RejoinAdminError(parseAdminRpcCode(error.message));
  const row = (Array.isArray(data) ? data[0] : data) as
    | { squad_id: string; user_id: string }
    | undefined;
  if (!row?.squad_id) throw new RejoinAdminError("unknown");
  return row;
}

export async function denySquadRejoinRequest(requestId: string): Promise<void> {
  const { error } = await supabase.rpc("deny_squad_rejoin", {
    p_request_id: requestId,
  });
  if (error) throw new RejoinAdminError(parseAdminRpcCode(error.message));
}

export type RejoinRequestErrorCode =
  | "rate_limited"
  | "not_self_leave"
  | "already_pending"
  | "squad_not_found"
  | "already_a_member"
  | "not_authenticated"
  | "unknown";

const REQUEST_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function assertRequestId(data: unknown): string {
  if (typeof data !== "string" || !REQUEST_ID_RE.test(data)) {
    throw new RejoinRequestError(
      "unknown",
      "Anfrage fehlgeschlagen: Ungültige Server-Antwort.",
    );
  }
  return data;
}

export type MyRejoinRequest = {
  id: string;
  squad_id: string;
  status: RejoinRequestStatus;
  requested_at: string;
  resolved_at: string | null;
};

const GERMAN_MESSAGES: Record<RejoinRequestErrorCode, string> = {
  rate_limited: "Du kannst derzeit keine neue Anfrage senden.",
  not_self_leave: "Du kannst diesem Squad so nicht erneut beitreten.",
  already_pending: "Deine Anfrage ist bereits ausstehend.",
  squad_not_found: "Dieses Squad existiert nicht mehr.",
  already_a_member: "Du bist bereits Mitglied dieses Squads.",
  not_authenticated: "Bitte melde dich an, um eine Anfrage zu senden.",
  unknown: "Anfrage fehlgeschlagen. Bitte versuche es später erneut.",
};

export class RejoinRequestError extends Error {
  readonly code: RejoinRequestErrorCode;
  readonly cooldownMs?: number;

  constructor(code: RejoinRequestErrorCode, message?: string, cooldownMs?: number) {
    super(message ?? GERMAN_MESSAGES[code]);
    this.name = "RejoinRequestError";
    this.code = code;
    this.cooldownMs = cooldownMs;
  }
}

function parseRpcCode(message: string): RejoinRequestErrorCode {
  const lower = message.toLowerCase();
  if (lower.includes("rate_limited")) return "rate_limited";
  if (lower.includes("not_self_leave")) return "not_self_leave";
  if (lower.includes("already_pending")) return "already_pending";
  if (lower.includes("squad_not_found")) return "squad_not_found";
  if (lower.includes("already_a_member")) return "already_a_member";
  if (lower.includes("not_authenticated")) return "not_authenticated";
  return "unknown";
}

function rejoinRpcError(error: { message: string; details?: string | null }): RejoinRequestError {
  const code = parseRpcCode(error.message);
  if (code === "unknown") {
    const detail = error.details?.trim();
    const fallback = error.message.trim();
    const message =
      detail && detail !== fallback ? `${fallback}: ${detail}` : fallback || undefined;
    return new RejoinRequestError(code, message);
  }
  return new RejoinRequestError(code);
}

export function formatRejoinCooldown(cooldownMs: number): string {
  if (cooldownMs <= 0) return "Du kannst jetzt erneut anfragen.";
  const totalMinutes = Math.ceil(cooldownMs / (60 * 1000));
  if (totalMinutes < 60) {
    return `Du kannst in ${totalMinutes} Minute${totalMinutes === 1 ? "" : "n"} erneut anfragen.`;
  }
  const hours = Math.ceil(totalMinutes / 60);
  return `Du kannst in ${hours} Stunde${hours === 1 ? "" : "n"} erneut anfragen.`;
}

export async function getLastRejoinRequestAt(squadId: string): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("squad_rejoin_requests")
    .select("requested_at")
    .eq("squad_id", squadId)
    .eq("user_id", user.id)
    .order("requested_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.requested_at ?? null;
}

export async function getRejoinCooldownMs(squadId: string): Promise<number> {
  const lastAt = await getLastRejoinRequestAt(squadId);
  if (!lastAt) return 0;
  const elapsed = Date.now() - new Date(lastAt).getTime();
  return Math.max(0, RATE_LIMIT_MS - elapsed);
}

export async function getMyRejoinRequest(squadId: string): Promise<MyRejoinRequest | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: pending, error: pendingErr } = await supabase
    .from("squad_rejoin_requests")
    .select("id, squad_id, status, requested_at, resolved_at")
    .eq("squad_id", squadId)
    .eq("user_id", user.id)
    .eq("status", "pending")
    .maybeSingle();

  if (pendingErr) throw pendingErr;
  if (pending) return pending as MyRejoinRequest;

  const { data: recent, error: recentErr } = await supabase
    .from("squad_rejoin_requests")
    .select("id, squad_id, status, requested_at, resolved_at")
    .eq("squad_id", squadId)
    .eq("user_id", user.id)
    .order("requested_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (recentErr) throw recentErr;
  return (recent as MyRejoinRequest | null) ?? null;
}

export async function requestSquadRejoin(squadId: string): Promise<string> {
  const { data, error } = await supabase.rpc("request_squad_rejoin", {
    p_squad_id: squadId,
  });

  if (error) {
    const parsed = rejoinRpcError(error);
    if (parsed.code === "rate_limited") {
      const cooldownMs = await getRejoinCooldownMs(squadId);
      throw new RejoinRequestError(
        "rate_limited",
        formatRejoinCooldown(cooldownMs),
        cooldownMs,
      );
    }
    throw parsed;
  }

  return assertRequestId(data);
}

function markLeftSquadPending(leftSquad: LeftSquad, requestId: string) {
  updateLeftSquad({
    id: leftSquad.id,
    pendingRequestId: requestId,
    reason: leftSquad.reason ?? "self_leave",
  });
}

/** Issue #1 + submit rejoin request; updates local ghost pending state on success. */
export async function submitLeftSquadRejoinRequest(leftSquad: LeftSquad): Promise<{
  requestId: string;
  alreadyPending: boolean;
}> {
  if (leftSquad.joinCode) {
    const squad = await fetchSquadByJoinCode(leftSquad.joinCode);
    if (!squad) {
      recordDissolvedSquad({ id: leftSquad.id, name: leftSquad.name });
      dismissLeftSquad(leftSquad.id);
      throw new RejoinRequestError("squad_not_found");
    }
  }

  try {
    const requestId = await requestSquadRejoin(leftSquad.id);
    markLeftSquadPending(leftSquad, requestId);
    return { requestId, alreadyPending: false };
  } catch (err) {
    if (err instanceof RejoinRequestError && err.code === "already_pending") {
      const existing = await getMyRejoinRequest(leftSquad.id);
      if (existing?.status === "pending" && REQUEST_ID_RE.test(existing.id)) {
        markLeftSquadPending(leftSquad, existing.id);
        return { requestId: existing.id, alreadyPending: true };
      }
    }
    throw err;
  }
}

